/**
 * Centralized error handling utility for the application
 */

/**
 * Logs API errors with consistent formatting
 * @param {Error} error - The error object
 * @param {string} context - Context where the error occurred
 * @param {boolean} suppress404 - Whether to suppress 404 error logging
 */
export function logApiError(error, context = 'API', suppress404 = true) {
    const timestamp = new Date().toISOString();
    
    // Skip logging 404s if suppress404 is true (default)
    if (suppress404 && error.isAxiosError && error.response && error.response.status === 404) {
        return;
    }
    
    console.error(`[${timestamp}] ${context} Error:`, error.message);
    
    if (error.isAxiosError && error.response) {
        console.error(`Status: ${error.response.status} ${error.response.statusText}`);
        console.error(`URL: ${error.config?.url}`);
        console.error(`Method: ${error.config?.method?.toUpperCase()}`);
        
        // Log specific error types
        if (error.response.status === 403) {
            console.error('⚠️  Authentication error - token may be expired or invalid');
        } else if (error.response.status === 404) {
            console.error('⚠️  Resource not found');
        } else if (error.response.status >= 500) {
            console.error('⚠️  Server error - backend service may be down');
        }
        
        // In production, you might want to send this to a monitoring service
        if (process.env.NODE_ENV === 'production') {
            // Example: send to monitoring service
            // sendToMonitoringService({
            //     error: error.message,
            //     status: error.response.status,
            //     url: error.config?.url,
            //     context,
            //     timestamp
            // });
        }
    } else if (error.request) {
        console.error('⚠️  Network error - no response received');
        console.error('Request details:', error.request);
    } else {
        console.error('⚠️  Request setup error:', error.message);
    }
}

/**
 * Creates a safe API wrapper that handles errors gracefully
 * @param {Function} apiFunction - The API function to wrap
 * @param {*} fallbackValue - Value to return on error
 * @param {string} context - Context for error logging
 */
export function safeApiCall(apiFunction, fallbackValue = null, context = 'API') {
    return async (...args) => {
        try {
            return await apiFunction(...args);
        } catch (error) {
            logApiError(error, context);
            return fallbackValue;
        }
    };
}

/**
 * Standard empty result structures for different API endpoints
 */
export const EMPTY_RESULTS = {
    search: {
        hits: {
            total: { value: 0 },
            hits: []
        },
        aggregations: {}
    },
    
    pagination: {
        data: [],
        meta: {
            pagination: {
                current_page: 1,
                total: 0,
                per_page: 20
            }
        }
    },
    
    list: [],
    
    single: null
};

/**
 * Determines if an error should trigger a fallback or be re-thrown
 * @param {Error} error - The error to check
 * @returns {boolean} - True if error should be handled gracefully
 */
export function shouldUseFallback(error) {
    if (!error.isAxiosError) return false;
    
    const status = error.response?.status;
    
    // Handle these errors gracefully
    const gracefulErrors = [403, 404, 429, 500, 502, 503, 504];
    
    return gracefulErrors.includes(status);
}
